import { useMemo } from 'react';
import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { Shipment } from 'common/src/utils/types';
import type { ShipmentLogbookSummary } from 'common/src/api/shipment-logbook';
import LogbookNoteEditor from 'common/src/components/shipments/LogbookNoteEditor';
import useUserStore from 'common/src/stores/useUserStore';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, EmptyState, LoadingState } from '../ui';
import { useShipmentLogbook } from '../../hooks/useShipmentLogbook';
import {
  groupShipmentLogbook,
  localCalendarDate,
  type LogbookEntry,
} from '../../utils/shipment-logbook';

export function ShipmentLogbookRowSummary({
  summary,
}: {
  summary: ShipmentLogbookSummary;
}) {
  const { t, currentLanguage } = useTranslation('shipments');
  if (!summary.last_activity_at) return null;
  return (
    <p className='mt-2 text-xs text-default-500'>
      {t('Última gestión')}:{' '}
      {new Intl.DateTimeFormat(currentLanguage === 'es' ? 'es-CL' : 'en-US', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(summary.last_activity_at))}
      {summary.last_actor ? ` · ${summary.last_actor}` : ''}
    </p>
  );
}

export default function ShipmentLogbook({
  shipment,
  isOpen,
  onBusyChange,
  onChanged,
  refreshVersion,
}: {
  shipment: Shipment;
  isOpen: boolean;
  onBusyChange: (busy: boolean) => void;
  onChanged?: () => void;
  refreshVersion?: number;
}) {
  const { t, currentLanguage } = useTranslation('shipments');
  const locale = currentLanguage === 'es' ? 'es-CL' : 'en-US';
  const userId = useUserStore((s) => s.userProfile?.id),
    authId = useUserStore((s) => s.session?.user.id);
  const {
    data,
    loading,
    readError,
    writeError,
    busy,
    noteCommitted,
    note,
    setNote,
    photos,
    setPhotos,
    loadLogbook,
    handleSaveNote,
  } = useShipmentLogbook({
    shipment,
    userId,
    authId,
    active: isOpen,
    onBusyChange,
    onChanged,
    refreshVersion,
  });
  const entries = useMemo(
    () => (data ? groupShipmentLogbook(data.comments) : []),
    [data]
  );
  const days = useMemo(() => {
    const result = new Map<string, LogbookEntry[]>();
    for (const entry of entries) {
      const day = localCalendarDate(new Date(entry.at));
      result.set(day, [...(result.get(day) || []), entry]);
    }
    return [...result.entries()];
  }, [entries]);
  return (
    <div className='flex flex-col gap-5 py-2'>
      {readError && (
        <Alert variant='danger' showIcon>
          <p>{readError}</p>
          <Button
            size='sm'
            variant='light'
            onPress={() => void loadLogbook()}
            isDisabled={busy}
          >
            {t('Reintentar')}
          </Button>
        </Alert>
      )}
      {writeError && (
        <Alert variant='warning' showIcon>
          {writeError}
        </Alert>
      )}
      {loading && <LoadingState label={t('Cargando bitácora...')} />}
      {data && (
        <>
          {data.state.can_read_notes ? (
            <div className='flex flex-col items-start gap-2'>
              <div className='w-full'>
                <LogbookNoteEditor
                  compact
                  note={note}
                  onNoteChange={setNote}
                  photos={photos}
                  onPhotosChange={setPhotos}
                  disabled={busy || loading || noteCommitted}
                  placeholder={t('Agregar nota...')}
                />
              </div>
              {(note.trim() || photos.length > 0 || noteCommitted) && (
                <Button
                  color='primary'
                  size='sm'
                  startContent={<Icon icon='tabler:plus' />}
                  isLoading={busy}
                  isDisabled={loading || !!readError}
                  onPress={() => void handleSaveNote()}
                >
                  {t(noteCommitted ? 'Reintentar fotos' : 'Guardar nota')}
                </Button>
              )}
            </div>
          ) : (
            <Alert variant='default' showIcon>
              {t('Las notas y fotos requieren permiso de Bitácora.')}
            </Alert>
          )}
          {entries.length === 0 ? (
            <EmptyState
              title={t('Sin actividad registrada')}
              description={t('Agrega una nota, una foto o registra precios.')}
            />
          ) : (
            <ol
              className='ml-3 border-l border-default-200'
              aria-label={t('Actividad del despacho')}
            >
              {days.map(([day, items]) => (
                <li key={day} className='pb-6 last:pb-0'>
                  <p className='pb-4 pl-6 text-xs font-semibold uppercase tracking-wide text-default-500'>
                    {new Intl.DateTimeFormat(locale, {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    }).format(new Date(items[0].at))}
                  </p>
                  <ol>
                    {items.map((entry) => (
                      <li
                        key={entry.key}
                        className='relative pb-6 pl-6 last:pb-0'
                      >
                        <span
                          aria-hidden='true'
                          className={`absolute -left-1.5 top-1.5 h-3 w-3 rounded-full ring-4 ring-content1 ${entry.kind === 'price' ? 'bg-warning' : 'bg-default-400'}`}
                        />
                        <article className='flex min-w-0 flex-col gap-2'>
                          <div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
                            <span className='text-sm font-semibold'>
                              {t(
                                entry.kind === 'price'
                                  ? 'Registro de precios'
                                  : 'Nota'
                              )}
                            </span>
                            <span className='text-xs text-default-500'>
                              {new Intl.DateTimeFormat(locale, {
                                hour: '2-digit',
                                minute: '2-digit',
                              }).format(new Date(entry.at))}
                              {entry.author ? ` · ${entry.author}` : ''}
                            </span>
                          </div>
                          {entry.notes.map((text, index) => (
                            <p
                              key={index}
                              className='whitespace-pre-wrap break-words text-sm text-default-500'
                            >
                              {text}
                            </p>
                          ))}
                          {entry.products.length > 0 && (
                            <ul className='flex flex-col gap-1 text-xs'>
                              {entry.products.map((product) => (
                                <li key={product.id}>
                                  {[
                                    product.variety,
                                    product.caliber,
                                    product.format,
                                    product.unit_weight
                                      ? `${product.unit_weight} kg`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                  {' — '}
                                  {product.unit_price} {product.local_currency}{' '}
                                  · {product.unit_count || 0} {t('bultos')}
                                </li>
                              ))}
                            </ul>
                          )}
                          {entry.photos.length > 0 && (
                            <div className='flex flex-wrap gap-2'>
                              {entry.photos.map((photo) => {
                                const url =
                                  data.photoUrls[
                                    `${photo.bucket}/${photo.path}`
                                  ];
                                return url ? (
                                  <a
                                    key={photo.id}
                                    href={url}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    <img
                                      src={url}
                                      alt={photo.name}
                                      loading='lazy'
                                      className='h-20 w-20 rounded-medium object-cover'
                                    />
                                  </a>
                                ) : (
                                  <span
                                    key={photo.id}
                                    className='text-xs text-default-500'
                                  >
                                    {t(
                                      'Foto no disponible. Recarga para reintentar.'
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </article>
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}
